import { FunctionComponent, useEffect, useState } from "react";
import {
  Form,
  Link1,
  Button,
  TextInput,
  styled,
  Navbar,
  Body2Regular
} from "@horizontal-org/shira-ui";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import shallow from "zustand/shallow";
import { useStore } from "../../store";
import backgroundSvg from '../../assets/Background.svg';

interface Props { }

export const LoginLayout: FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user, login, loginStatus, setLoginStatus } = useStore(
    (state) => ({
      user: state.user,
      login: state.login,
      loginStatus: state.loginStatus,
      setLoginStatus: state.setLoginStatus
    }),
    shallow
  );

  const [email, handleEmail] = useState("");
  const [pass, handlePass] = useState("");

  useEffect(() => {
    if (user) {
      navigate("/", { state: { fromLogin: true }, replace: true });
    }
  }, [navigate, user]);

  const description = (
    <>
      <div id="login-description-container">
        <span id="login-description">{t('login.subtitle')}</span>
      </div>
    </>
  );
  return (
    <Container>
      <Navbar
        translatedTexts={{ home: "", about: "", menu: "", logIn: "Sign up", createSpace: "" }}
        onNavigate={() => navigate("/get-started")}
      />
      <ContentWrapper>
        <BackgroundPattern />
        <StyledForm
          title={t('login.title')}
          description={description}
          onSubmit={(e) => {
            e.preventDefault()
            login(email, pass)
          }}
        >
          <InputsContainer>
            <TextInput
              id="email-input"
              label={t('login.email_placeholder')}
              value={email}
              onChange={(e) => {
                if (loginStatus === 'error') {
                  setLoginStatus('idle');
                }
                handleEmail(e.target.value)
              }}
            />
            <TextInput
              id="password-input"
              type="password"
              label={t('login.password_placeholder')}
              value={pass}
              onChange={(e) => {
                if (loginStatus === 'error') {
                  setLoginStatus('idle');
                }
                handlePass(e.target.value)
              }}
            />
            {loginStatus === 'error' && <ErrorText id="login-error">{t('login.error_message')}</ErrorText>}
          </InputsContainer>

          <ForgotPasswordContainer>
            <Link1
              id="reset-password-link"
              href="/reset-password"
              onClick={(e) => {
                e.preventDefault();
                navigate("/reset-password");
              }}
            >
              {t('login.reset_password')}
            </Link1>
          </ForgotPasswordContainer>

          <ButtonContainer>
            <Button
              id="login-button"
              text={loginStatus === 'loading' ? t('login.loading_button') : t('buttons.login')}
              type="primary"
              disabled={!(email && pass) || loginStatus === 'loading'}
              onClick={(e) => {
                e.preventDefault();
                login(email, pass);
              }}
            />
          </ButtonContainer>
        </StyledForm>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100vh;
  padding: 24px;
  display: flex;
  flex-direction: column;
  position: relative; 

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
      padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledForm = styled(Form)`
  position: relative;
  z-index:1;
`;

const BackgroundPattern = styled.div`
  background-image: url(${backgroundSvg});
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.15;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    display: none;  // Hides the background on mobile
  }
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ForgotPasswordContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 100%;
    
    button {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`;

const ErrorText = styled(Body2Regular)`
  color: ${(props) => props.theme.colors.error7};
  margin-top: -16px
`